import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { APIService } from '@services/api/common-services';
import { APICONFIG } from '@config/api.config';
import { TaskListProductsData } from '@services/hooks/site-leads';
import { ToastType, useTosterStore } from '@store/toster';
import { useAddNewSiteStore } from '@store/lms/addNewSiteStore';
import { useSiteListQuery } from '@services/hooks/lms/siteList';

interface IProductRecommendationNavigationParams {
	grade: string;
	reason: string;
	type_of_contract: string;
	glue: string;
	membershipno: string;
	isConvertingUpcomingToActive: boolean;
}

export const useProductRecommendationNavigation = (): {
	handleProductRecommendationAndNavigate: (params: IProductRecommendationNavigationParams) => Promise<void>;
} => {
	const navigate = useNavigate();
	const { setToaster } = useTosterStore();
	const { resetAddNewSiteValues } = useAddNewSiteStore();
	const { refetch: refetchLmsSiteList } = useSiteListQuery();

	const handleProductRecommendationAndNavigate = useCallback(
		async (params: IProductRecommendationNavigationParams) => {
			const { isConvertingUpcomingToActive, ...navigationData } = params;

			// Skip API call for converting upcoming to active
			if (isConvertingUpcomingToActive) {
				resetAddNewSiteValues();
				refetchLmsSiteList?.();
				navigate('/');
				return;
			}

			try {
				// Call product recommendation API
				const productRecommendationsData = await APIService.getData<TaskListProductsData>(
					APICONFIG.LMS_PRODUCT_RECOMMENDATIONS,
					{
						grade: navigationData.grade,
						reason: navigationData.reason,
						type_of_contract: navigationData.type_of_contract,
						glue: navigationData.glue,
						membershipno: navigationData.membershipno ?? '',
					}
				);

				// Check if product recommendations exist
				const productList = productRecommendationsData?.data?.product_recommendations;

				// Explicit check: empty array [] should redirect to dashboard
				const isArrayWithItems = Array.isArray(productList) && productList.length > 0;

				// Additional validation: if array exists with items, check if items are valid
				let hasValidProductRecommendations = false;
				if (isArrayWithItems && productList) {
					hasValidProductRecommendations = productList.some(
						(product) => product && (product.id || product.product_name)
					);
				}

				if (hasValidProductRecommendations) {
					// Navigate to product recommendations screen with data
					navigate('/lms/product-recommendations', {
						state: navigationData,
					});
				} else {
					// No product recommendations, redirect to dashboard
					setToaster(true, ToastType.success, 'Site lead added successfully!');
					resetAddNewSiteValues();
					refetchLmsSiteList?.();
					navigate('/');
					return;
				}

				resetAddNewSiteValues();
				refetchLmsSiteList?.();
			} catch (error) {
				// On error, redirect to dashboard
				setToaster(true, ToastType.error, 'Failed to fetch product recommendations. Redirecting to dashboard.');
				resetAddNewSiteValues();
				refetchLmsSiteList?.();
				navigate('/');
			}
		},
		[navigate, resetAddNewSiteValues, refetchLmsSiteList, setToaster]
	);

	return { handleProductRecommendationAndNavigate };
};
